/**
 * Product Recommendation Service
 * Client-side recommendation engine based on browsing and order history
 */

class RecommendationService {
  constructor() {
    this.BROWSING_HISTORY_KEY = 'browsingHistory';
    this.ORDER_HISTORY_KEY = 'orderHistory';
    this.MAX_BROWSING_HISTORY = 50; // Keep last 50 viewed products
    this.MAX_RECOMMENDATIONS = 12;
  }

  /**
   * Track product view
   */
  trackProductView(product) {
    if (!product || !product.id) return;

    const history = this.getBrowsingHistory();
    
    // Remove if already exists (to move to top)
    const filtered = history.filter(item => item.id !== product.id);
    
    // Add to beginning
    const productData = {
      id: product.id,
      name: product.name,
      brand: product.brand,
      category: product.category,
      subCategory: product.subCategory,
      price: product.price || product.mrp,
      image: product.image || product.images?.[0],
      partNumber: product.partNumber,
      viewedAt: new Date().toISOString()
    };

    const updated = [productData, ...filtered].slice(0, this.MAX_BROWSING_HISTORY);
    localStorage.setItem(this.BROWSING_HISTORY_KEY, JSON.stringify(updated));
  }

  /**
   * Track order (from order history)
   */
  trackOrder(order) {
    if (!order || !order.id) return;

    const history = this.getOrderHistory();
    
    // Check if order already tracked
    if (history.find(o => o.id === order.id)) return;

    const orderData = {
      id: order.id,
      items: order.items || [],
      packages: order.packages || [],
      date: order.date || order.createdAt,
      total: order.total,
      status: order.status
    };

    const updated = [orderData, ...history].slice(0, 50); // Keep last 50 orders
    localStorage.setItem(this.ORDER_HISTORY_KEY, JSON.stringify(updated));
  }

  /**
   * Get browsing history
   */
  getBrowsingHistory() {
    try {
      const stored = localStorage.getItem(this.BROWSING_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading browsing history:', error);
      return [];
    }
  }

  /**
   * Get order history
   */
  getOrderHistory() {
    try {
      const stored = localStorage.getItem(this.ORDER_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading order history:', error);
      return [];
    }
  }

  /**
   * Get all products from orders
   */
  getOrderedProducts() {
    const orders = this.getOrderHistory();
    const products = [];

    orders.forEach(order => {
      // Extract from items
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          if (item.productId || item.id) {
            products.push({
              id: item.productId || item.id,
              name: item.name,
              brand: item.brand,
              category: item.category,
              quantity: item.quantity,
              orderedAt: order.date
            });
          }
        });
      }

      // Extract from packages
      if (order.packages && Array.isArray(order.packages)) {
        order.packages.forEach(pkg => {
          if (pkg.items && Array.isArray(pkg.items)) {
            pkg.items.forEach(item => {
              if (item.productId || item.id) {
                products.push({
                  id: item.productId || item.id,
                  name: item.name,
                  brand: item.brand,
                  category: item.category,
                  quantity: item.quantity,
                  orderedAt: order.date
                });
              }
            });
          }
        });
      }
    });

    return products;
  }

  /**
   * Get user preferences (categories, brands)
   */
  getUserPreferences() {
    const browsingHistory = this.getBrowsingHistory();
    const orderedProducts = this.getOrderedProducts();

    const categoryCount = {};
    const brandCount = {};

    // Count from browsing history
    browsingHistory.forEach(item => {
      if (item.category) {
        categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;
      }
      if (item.brand) {
        brandCount[item.brand] = (brandCount[item.brand] || 0) + 1;
      }
    });

    // Count from order history (weighted more)
    orderedProducts.forEach(item => {
      if (item.category) {
        categoryCount[item.category] = (categoryCount[item.category] || 0) + 3;
      }
      if (item.brand) {
        brandCount[item.brand] = (brandCount[item.brand] || 0) + 3;
      }
    });

    const topCategories = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([category]) => category);

    const topBrands = Object.entries(brandCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([brand]) => brand);

    return { categories: topCategories, brands: topBrands };
  }

  /**
   * Generate recommendations based on browsing and order history
   * @param {Array} allProducts - All available products
   * @param {Object} currentProduct - Current product (optional, for "similar products")
   * @returns {Array} Recommended products
   */
  getRecommendations(allProducts = [], currentProduct = null) {
    if (!allProducts || allProducts.length === 0) return [];

    const browsingHistory = this.getBrowsingHistory();
    const orderedProducts = this.getOrderedProducts();
    const preferences = this.getUserPreferences();

    // Get viewed/ordered product IDs to exclude
    const viewedIds = new Set(browsingHistory.map(item => item.id));
    const orderedIds = new Set(orderedProducts.map(item => item.id));

    // Score products
    const scoredProducts = allProducts
      .filter(product => {
        // Exclude current product if provided
        if (currentProduct && product.id === currentProduct.id) return false;
        // Exclude already viewed/ordered products
        return !viewedIds.has(product.id) && !orderedIds.has(product.id);
      })
      .map(product => ({
        product,
        score: this.calculateScore(product, browsingHistory, orderedProducts, preferences, currentProduct)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, this.MAX_RECOMMENDATIONS)
      .map(item => item.product);

    // If we don't have enough recommendations, add popular products
    if (scoredProducts.length < this.MAX_RECOMMENDATIONS) {
      const popularProducts = allProducts
        .filter(product => {
          if (currentProduct && product.id === currentProduct.id) return false;
          return !viewedIds.has(product.id) && !orderedIds.has(product.id);
        })
        .slice(0, this.MAX_RECOMMENDATIONS - scoredProducts.length);
      
      scoredProducts.push(...popularProducts);
    }

    return scoredProducts.slice(0, this.MAX_RECOMMENDATIONS);
  }

  /**
   * Calculate recommendation score for a product
   */
  calculateScore(product, browsingHistory, orderedProducts, preferences, currentProduct) {
    let score = 0;

    // Category match (high weight)
    if (preferences.categories.includes(product.category)) {
      score += 10;
    }

    // Brand match (high weight)
    if (preferences.brands.includes(product.brand)) {
      score += 10;
    }

    // Similar to current product
    if (currentProduct) {
      if (product.category === currentProduct.category) {
        score += 15;
      }
      if (product.brand === currentProduct.brand) {
        score += 10;
      }
      if (product.subCategory === currentProduct.subCategory) {
        score += 5;
      }
    }

    // Recently browsed similar products
    browsingHistory.slice(0, 10).forEach(item => {
      if (product.category === item.category) score += 3;
      if (product.brand === item.brand) score += 2;
    });

    // Ordered similar products
    orderedProducts.forEach(item => {
      if (product.category === item.category) score += 5;
      if (product.brand === item.brand) score += 3;
    });

    // Price range preference (if user has ordered products in similar price range)
    if (orderedProducts.length > 0) {
      const avgOrderPrice = orderedProducts.reduce((sum, item) => {
        const price = item.price || 0;
        return sum + price;
      }, 0) / orderedProducts.length;

      const priceDiff = Math.abs((product.price || 0) - avgOrderPrice);
      const priceRange = avgOrderPrice * 0.5; // 50% range
      if (priceDiff <= priceRange) {
        score += 2;
      }
    }

    // Popular products (rating/reviews)
    if (product.rating && product.rating >= 4) {
      score += 2;
    }

    return score;
  }

  /**
   * Get "You may also like" recommendations for a specific product
   */
  getSimilarProducts(allProducts, product) {
    if (!product || !allProducts) return [];

    return allProducts
      .filter(p => p.id !== product.id)
      .map(p => ({
        product: p,
        score: this.calculateSimilarityScore(p, product)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map(item => item.product);
  }

  /**
   * Calculate similarity score between two products
   */
  calculateSimilarityScore(product1, product2) {
    let score = 0;

    if (product1.category === product2.category) score += 10;
    if (product1.brand === product2.brand) score += 8;
    if (product1.subCategory === product2.subCategory) score += 5;

    // Price similarity (within 30% range)
    const price1 = product1.price || 0;
    const price2 = product2.price || 0;
    if (price1 > 0 && price2 > 0) {
      const priceDiff = Math.abs(price1 - price2) / Math.max(price1, price2);
      if (priceDiff <= 0.3) score += 3;
    }

    return score;
  }

  /**
   * Clear browsing history
   */
  clearBrowsingHistory() {
    localStorage.removeItem(this.BROWSING_HISTORY_KEY);
  }

  /**
   * Clear order history
   */
  clearOrderHistory() {
    localStorage.removeItem(this.ORDER_HISTORY_KEY);
  }
}

// Export singleton instance
export const recommendationService = new RecommendationService();
export default recommendationService;

